import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  ModelSignal,
  OnInit,
  Optional,
  OutputEmitterRef,
  Signal,
  computed,
  input,
  model,
  output,
} from '@angular/core';

import { InsightsBaseEvent, InsightsEvent, InsightsEventsTrackingService, insightsNamedEvent } from '@core/shared/util';
import { FilterSelectionOption, SingleSelectionGroupComponent } from '@core/ui';
import { SingleSelectFacet } from '@mp/shared/facets/domain';
import { BucketIconFn, FacetExpansionStateService, mapFacetBucketsToOptions } from '@mp/shared/facets/util';

import { SingleSelectFacetInsightsEventsConfig } from '../../models';

@Component({
  selector: 'mp-single-select-facet',
  templateUrl: './single-select-facet.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SingleSelectionGroupComponent],
  providers: [FacetExpansionStateService],
})
export class SingleSelectFacetComponent<TData extends object | unknown = unknown> implements OnInit {
  /**
   * The SingleSelectFacet object to display.
   */
  readonly facet: InputSignal<SingleSelectFacet<TData>> = input.required<SingleSelectFacet<TData>>();

  /**
   * Selected value of filter
   */
  readonly selectedValue: ModelSignal<string | undefined> = model.required<string | undefined>();

  /**
   * Specifies the name of the filter.
   */
  readonly filterName: InputSignal<string> = input.required<string>();

  /**
   * Specifies the icon of the filter component.
   */
  readonly icon: InputSignal<string | undefined> = input<string>();

  /**
   * If the current selection state should be stored, provide a unique key within the current instance
   * of `FacetSelectionService`. This is a constant value and must not be changed.
   */
  readonly selectionKey: InputSignal<string | undefined> = input<string>();

  readonly bucketIconFn: InputSignal<BucketIconFn<TData> | undefined> = input<BucketIconFn<TData>>();

  readonly trackInsightsEvents: InputSignal<boolean> = input<boolean>(false);

  /**
   * Emits on selection change and provides the selected value of the facet.
   */
  readonly changed: OutputEmitterRef<string> = output<string>();

  protected readonly filterOptions: Signal<FilterSelectionOption<string>[]> = computed(() =>
    mapFacetBucketsToOptions(this.facet().buckets, this.bucketIconFn()),
  );

  private readonly insightsEventsConfig: Signal<SingleSelectFacetInsightsEventsConfig> = computed(() =>
    this.getFilterInsightsEventsConfig(this.filterName()),
  );

  get isExpanded(): boolean {
    return this.facetExpansionStateService.isExpanded();
  }

  constructor(
    private readonly facetExpansionStateService: FacetExpansionStateService,
    @Optional() private readonly insightsEventsTrackingService: InsightsEventsTrackingService | null,
  ) {}

  ngOnInit(): void {
    this.facetExpansionStateService.setSelectionKey(this.selectionKey());
  }

  onSelectionChanged(value: string): void {
    this.selectedValue.set(value);
    this.changed.emit(value);
  }

  onOptionSelected({ label }: FilterSelectionOption<string>): void {
    this.trackValueSelectEvent(label);
  }

  onExpandedChange(expanded: boolean): void {
    this.facetExpansionStateService.setExpanded(expanded);
    if (!this.trackInsightsEvents()) {
      return;
    }

    const filterExpansionEvent: InsightsEvent = expanded
      ? this.insightsEventsConfig().filterExpand
      : this.insightsEventsConfig().filterCollapse;
    this.insightsEventsTrackingService?.trackEvent(filterExpansionEvent);
  }

  trackValueSelectEvent(valueLabel: string): void {
    if (this.trackInsightsEvents()) {
      this.insightsEventsTrackingService?.trackEvent(this.insightsEventsConfig().valueSelect, {
        value: valueLabel,
      });
    }
  }

  private getFilterInsightsEventsConfig(filterName: string): SingleSelectFacetInsightsEventsConfig {
    return {
      filterExpand: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_EXPAND),
      filterCollapse: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_COLLAPSE),
      valueSelect: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_CHANGE),
    };
  }
}
